import { useTranslation } from 'react-i18next';
import { Accordion, Group, Stack, Text } from '@mantine/core';
import DocRow from '@/components/app/deals/steps/forwarding/DocRow';
import StageSection from '@/components/app/deals/steps/forwarding/StageSection';
import VehicleRow from '@/components/app/deals/steps/forwarding/VehicleRow';
import { countVehiclesUnloaded, isDailyUpd, remaining } from '@/lib/forwardingView';
import { fmtFwdDate } from '@/lib/forwarding';
import { formatMoney } from '@/lib/format';
import type { ForwardingInfo } from '@/types/deals';

interface DeliveredWeightProps {
  readonly info: ForwardingInfo;
}

// Этап «Доставленный вес»: выгружено на терминале; вес — по весам принимающей стороны (кг). День →
// карточки ТС (выгруж. вес + дата отгрузки) + документы дня (Реестр принятого зерна, Расчёт качества).
// Расхождение «отгружено↔доставлено» подсвечиваем (естественная усушка/приёмка по весам терминала).
export default function DeliveredWeight({ info }: DeliveredWeightProps) {
  const { t } = useTranslation();
  const unloaded = info.unloadedDeliveryInfo;
  const days = unloaded?.unloadedDays ?? [];

  if (!days.length && !unloaded?.unloadedWeightKg) {
    return null;
  }

  const loadedKg = info.loadedDeliveryInfo?.loadedWeightKg;
  const delta = loadedKg != null && unloaded?.unloadedWeightKg != null ? loadedKg - unloaded.unloadedWeightKg : 0;

  return (
    <StageSection
      title={t('app.deals.forwarding.unloaded')}
      unit={t('app.deals.forwarding.kg')}
      doneLabel={t('app.deals.forwarding.unloadedDone')}
      done={unloaded?.unloadedWeightKg}
      total={unloaded?.totalWeightKg}
      remainingLabel={t('app.deals.forwarding.unloadedRemaining')}
      remaining={remaining(unloaded?.totalWeightKg, unloaded?.unloadedWeightKg)}
      count={countVehiclesUnloaded(info)}
      countLabel={t('app.deals.forwarding.vehiclesShort')}
      emptyText={days.length ? undefined : t('app.deals.forwarding.unloadedEmpty')}
      compact
    >
      <Stack gap="sm">
        {delta > 0 ? (
          <Text fz="xs" c="orange.7">
            {t('app.deals.forwarding.discrepancy', { value: `${formatMoney(delta)} ${t('app.deals.forwarding.kg')}` })}
          </Text>
        ) : null}
        <Accordion variant="separated" radius="md" multiple defaultValue={[String(days.length - 1)]}>
          {days.map((day, i) => (
            <Accordion.Item key={day.unloadedDate ?? i} value={String(i)}>
              <Accordion.Control>
                <Group gap="md" wrap="nowrap">
                  <Text fz="sm" fw={500}>
                    {fmtFwdDate(day.unloadedDate) ?? '—'}
                  </Text>
                  <Text fz="sm" c="gray.7">
                    {day.vehicles?.length ?? 0} {t('app.deals.forwarding.vehiclesShort')}
                  </Text>
                  <Text fz="sm" c="gray.8">
                    {formatMoney(day.unloadedWeightKg)} {t('app.deals.forwarding.kg')}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  {(day.vehicles ?? []).map((v, vi) => (
                    <VehicleRow
                      key={v.ttnId ?? `${i}-${vi}`}
                      vehicle={{ ...v, weightKg: v.unloadedWeightKg }}
                      index={vi + 1}
                    />
                  ))}
                  {/* УПД исключаем — он в секции «Подпись УПД». Здесь — реестр принятого / расчёт качества. */}
                  {(day.documents ?? [])
                    .filter((doc) => !isDailyUpd(doc))
                    .map((doc, di) => (
                      <DocRow key={doc.id ?? `${i}-${di}`} doc={doc} />
                    ))}
                  {(day.documents ?? []).length === 0 ? (
                    <Text fz="xs" c="gray.5">
                      {t('app.deals.forwarding.docsAwaitPeriod')}
                    </Text>
                  ) : null}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </StageSection>
  );
}
